import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import FormSelect from "../../../Components/v2/Form/FormSelect";
import FormInput from "../../../Components/v2/Form/FormInput";
import FormRadioGroup from "../../../Components/v2/Form/FormRadioGroup";
import TaxCalculationExamples from "../../../Components/TaxCalculationExamples";
import { PPH_23_DATA, PPH_23_OBJECT_CODES } from "../../../Constants";
import {
  HAS_NPPN_OPTIONS,
  HAS_NPWP_OPTIONS,
} from "../../../Constants/calculator";
import { PPH23_KB_KEYS } from "../../../Constants/pph23KnowledgeBase";
import { PPH_23_EXAMPLES } from "../../../Constants/pph23Examples";
import { formatMoney, parseCurrencyValue } from "../../../Utils/StringUtils";
import {
  CalculatorCard,
  DetailBox,
  DetailRow,
  EmptyCalculation,
  FormulaRow,
  LedgerSection,
  ResetButton,
  ResultHeadline,
  ResultRow,
} from "../../../Components/Calculator/components";
import KnowledgeBaseSection from "../../../Components/Calculator/KnowledgeBaseSection";

const CalculatorPph23 = () => {
  const { t } = useTranslation();
  const resultRef = useRef(null);

  const [objectCode, setObjectCode] = useState(null);
  const [grossAmount, setGrossAmount] = useState("");
  const [hasNPWP, setHasNPWP] = useState(HAS_NPWP_OPTIONS[0]);
  const [hasNPPN, setHasNPPN] = useState(HAS_NPPN_OPTIONS[1]);
  const [errors, setErrors] = useState({});

  const objectCodeOptions = useMemo(() => {
    const options15 = [];
    const options2 = [];

    PPH_23_OBJECT_CODES.forEach((code) => {
      const data = PPH_23_DATA[code];
      const option = {
        value: code,
        label: code + " - " + t(`taxCalculator.pph23Objects.${code}`),
      };

      if (data.baseRate === 0.15) {
        options15.push(option);
      } else if (data.baseRate === 0.02) {
        options2.push(option);
      }
    });

    return [
      {
        label: "taxCalculator.pph23.rateGroups.rate15",
        options: options15,
      },
      {
        label: "taxCalculator.pph23.rateGroups.rate2",
        options: options2,
      },
    ];
  }, [t]);

  const calculations = useMemo(() => {
    const parsedGross = parseCurrencyValue(grossAmount);

    if (
      !objectCode?.value ||
      !PPH_23_DATA[objectCode?.value] ||
      parsedGross <= 0
    ) {
      return null;
    }

    const data = PPH_23_DATA[objectCode.value];
    const isRoyalty = objectCode.value === "24-103-01";
    const isUsingNPPN = isRoyalty && hasNPWP?.value && hasNPPN?.value;

    let baseRate = data.baseRate;
    let appliedRate;

    if (isUsingNPPN) {
      // Tarif efektif 6% untuk royalti NPPN (15% × 40%)
      appliedRate = 0.06;
    } else {
      const multiplier = hasNPWP?.value ? 1 : 2;
      appliedRate = baseRate * multiplier;
    }

    const taxAmount = Math.round(parsedGross * appliedRate);
    const netAmount = parsedGross - taxAmount;

    return {
      objectCode: objectCode.value,
      description: t(`taxCalculator.pph23Objects.${objectCode.value}`),
      grossAmount: parsedGross,
      hasNPWP: hasNPWP?.value,
      hasNPPN: hasNPPN?.value,
      isRoyalty,
      isUsingNPPN,
      baseRate,
      appliedRate,
      taxAmount,
      netAmount,
      ratePercentage: baseRate * 100,
      appliedRatePercentage: appliedRate * 100,
    };
  }, [objectCode, grossAmount, hasNPWP, hasNPPN, t]);

  const handleReset = () => {
    setObjectCode(null);
    setGrossAmount("");
    setHasNPWP(HAS_NPWP_OPTIONS[0]);
    setHasNPPN(HAS_NPPN_OPTIONS[1]);
    setErrors({});
  };

  const handleObjectCodeChange = (option) => {
    setObjectCode(option);
    // Reset NPPN when object code changes
    setHasNPPN(HAS_NPPN_OPTIONS[1]);
    if (errors.objectCode) {
      setErrors((prev) => ({ ...prev, objectCode: null }));
    }
  };

  const handleGrossAmountChange = (e) => {
    setGrossAmount(e.target.value);
    if (errors.grossAmount) {
      setErrors((prev) => ({ ...prev, grossAmount: null }));
    }
  };

  const handleSelectExample = (exampleData) => {
    // Directly set all fields to either example data or default values
    const foundOption = objectCodeOptions
      .flatMap((group) => group.options)
      .find((opt) => opt.value === exampleData.objectCode);

    setObjectCode(foundOption || null);
    setGrossAmount(
      exampleData.grossAmount ? formatMoney(exampleData.grossAmount) : "",
    );

    const npwpOption = HAS_NPWP_OPTIONS.find(
      (opt) => opt.value === exampleData.hasNPWP,
    );
    setHasNPWP(npwpOption || HAS_NPWP_OPTIONS[0]);
    const nppnOption = HAS_NPPN_OPTIONS.find(
      (opt) => opt.value === exampleData.hasNPPN,
    );
    setHasNPPN(nppnOption || HAS_NPPN_OPTIONS[1]);
    setErrors({});

    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const isValidCalculation = calculations !== null;

  return (
    <div className="space-y-5">
      <TaxCalculationExamples
        examples={PPH_23_EXAMPLES}
        onSelectExample={handleSelectExample}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Input Section */}
        <CalculatorCard title={t("taxCalculator.inputData")}>
          <div className="space-y-4">
            <FormSelect
              label="taxCalculator.pph23.fields.objectCode"
              value={objectCode}
              onChange={handleObjectCodeChange}
              options={objectCodeOptions}
              placeholder={t("taxCalculator.pph23.placeholders.objectCode")}
              searchable
              error={errors.objectCode}
              onClear={() => setObjectCode(null)}
            />

            <div>
              <FormInput
                label="taxCalculator.pph23.fields.grossIncome"
                id="grossAmount"
                name="grossAmount"
                value={grossAmount}
                onChange={handleGrossAmountChange}
                placeholder="0"
                numberFormat={{
                  thousandSeparator: ".",
                  decimalSeparator: ",",
                  allowNegative: false,
                  prefix: "Rp ",
                }}
              />
              {errors.grossAmount && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.grossAmount}
                </p>
              )}
            </div>

            <FormRadioGroup
              label="taxCalculator.pph23.fields.hasNPWP"
              value={hasNPWP}
              onChange={(value) => {
                setHasNPWP(value);
                // Reset NPPN when NPWP changes to No
                if (!value.value) {
                  setHasNPPN(HAS_NPPN_OPTIONS[1]);
                }
              }}
              options={HAS_NPWP_OPTIONS}
              gridClassName="grid-cols-2"
              verticalSpace="py-2"
            />

            {objectCode?.value === "24-103-01" && hasNPWP?.value && (
              <FormRadioGroup
                label="taxCalculator.pph23.fields.hasNPPN"
                value={hasNPPN}
                onChange={setHasNPPN}
                options={HAS_NPPN_OPTIONS}
                gridClassName="grid-cols-2"
                verticalSpace="py-2"
              />
            )}
          </div>
        </CalculatorCard>

        {/* Results Section */}
        <div ref={resultRef}>
          <CalculatorCard title={t("taxCalculator.results.title")}>
            {!isValidCalculation ? (
              <EmptyCalculation
                message={t("taxCalculator.pph23.noCalculation")}
              />
            ) : (
              <>
                <ResultHeadline
                  label={t("taxCalculator.pph23.results.pph23")}
                  value={formatMoney(calculations.taxAmount)}
                  caption={calculations.description}
                />

                <LedgerSection title={t("taxCalculator.results.taxBasis")}>
                  <ResultRow
                    label={t("taxCalculator.pph23.fields.objectCode")}
                    value={calculations.objectCode}
                  />
                  <ResultRow
                    label={t("taxCalculator.pph23.fields.grossIncome")}
                    value={formatMoney(calculations.grossAmount)}
                  />
                  <ResultRow
                    label={t("taxCalculator.pph23.results.rate")}
                    value={`${calculations.appliedRatePercentage.toFixed(0)}%`}
                  />
                </LedgerSection>

                <DetailBox title={t("taxCalculator.calculations.detail")}>
                  <DetailRow
                    label={t("taxCalculator.pph23.results.baseRate")}
                    value={`${calculations.ratePercentage.toFixed(0)}%`}
                  />
                  {calculations.isUsingNPPN ? (
                    <>
                      <DetailRow
                        label={t("taxCalculator.pph23.results.nppnRate")}
                        value="40% (NPPN)"
                      />
                      <DetailRow
                        label={t("taxCalculator.pph23.results.effectiveRate")}
                        value="6% (15% × 40%)"
                      />
                    </>
                  ) : (
                    !calculations.hasNPWP && (
                      <DetailRow
                        label={t(
                          "taxCalculator.pph23.results.noNpwpMultiplier",
                        )}
                        value="2x"
                      />
                    )
                  )}
                  <FormulaRow
                    label={t("taxCalculator.pph23.results.formula")}
                    value={`${calculations.appliedRatePercentage.toFixed(0)}% × ${formatMoney(calculations.grossAmount)}`}
                  />
                </DetailBox>

                <ResultRow
                  bold
                  label={t("taxCalculator.pph23.results.netAmount")}
                  value={formatMoney(calculations.netAmount)}
                />

                <ResultRow
                  total
                  label={t("taxCalculator.pph23.results.pph23")}
                  value={formatMoney(calculations.taxAmount)}
                />
              </>
            )}
          </CalculatorCard>
        </div>
      </div>

      <ResetButton onClick={handleReset} />

      {/* Knowledge Base / Educational SEO Footer */}
      <KnowledgeBaseSection keys={PPH23_KB_KEYS} />
    </div>
  );
};

export default CalculatorPph23;
